import { useEffect, useRef, useState } from 'react';

import { onLinkClick } from '../app/router.js';
import { useT } from '../i18n/index.js';
import { isTeacher, logout, useMe } from '../state/auth.js';
import { Icon } from './Icon.js';

/** Top-bar account entry: "Log in" for an anonymous browser, else the user's menu. */
export function AccountMenu() {
  const t = useT();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (me === undefined) return null;
  if (me === null) {
    return (
      <a href="/login" onClick={onLinkClick} className="btn btn-sm account-login">
        <Icon name="user" size={14} />
        {t.menu.login}
      </a>
    );
  }

  const link = (href: string, icon: Parameters<typeof Icon>[0]['name'], label: string) => (
    <a
      role="menuitem"
      href={href}
      onClick={(e) => {
        setOpen(false);
        onLinkClick(e);
      }}
    >
      <Icon name={icon} size={15} />
      {label}
    </a>
  );

  return (
    <div className="account-menu" ref={root}>
      <button
        className="account-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t.menu.label}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="account-initial" aria-hidden="true">
          {me.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="account-name">{me.displayName}</span>
      </button>
      {open && (
        <div className="account-popover" role="menu">
          <div className="account-popover-head">
            <strong>{me.displayName}</strong>
            <span>{me.email}</span>
          </div>
          {link('/account', 'user', t.menu.account)}
          {link('/classes', 'users', t.menu.classes)}
          {isTeacher(me) && link('/my-exercises', 'book', t.menu.exercises)}
          {me.role === 'admin' && link('/admin', 'shield', t.menu.admin)}
          <button role="menuitem" onClick={() => void logout()}>
            <Icon name="logout" size={15} />
            {t.menu.logout}
          </button>
        </div>
      )}
    </div>
  );
}
